import { useState, useEffect } from 'react';
import { Play } from 'lucide-react';
import { resolveImageUrl, getCachedUrl } from '@/hooks/useImageUrls';
import { isVideoUrl } from '@/lib/mediaUtils';

interface ListingThumbnailProps {
  src: string;
  alt: string;
  className?: string;
}

const ListingThumbnail = ({ src, alt, className = '' }: ListingThumbnailProps) => {
  const cachedInitial = getCachedUrl(src);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(cachedInitial);
  const [loading, setLoading] = useState(!cachedInitial);
  const [error, setError] = useState(false);
  const isVideo = isVideoUrl(src);

  useEffect(() => {
    let mounted = true;
    
    const resolve = async () => {
      setLoading(true);
      setError(false);
      setResolvedUrl(null);
      const url = await resolveImageUrl(src);
      if (mounted) {
        setResolvedUrl(url);
        setLoading(false);
      }
    };
    
    resolve();
    
    return () => {
      mounted = false;
    };
  }, [src]);

  if (error) {
    return (
      <div className={`bg-muted flex items-center justify-center ${className}`}>
        <span className="text-xs text-muted-foreground">—</span>
      </div>
    );
  }

  if (loading || !resolvedUrl) {
    return (
      <div className={`bg-muted animate-pulse ${className}`} />
    );
  }

  if (isVideo) {
    return (
      <div className={`relative ${className}`}>
        <video
          src={resolvedUrl}
          className="w-full h-full object-cover object-center"
          muted
          playsInline
          preload="metadata"
          onError={() => setError(true)}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <Play className="w-5 h-5 text-white fill-white" />
        </div>
      </div>
    );
  }

  return (
    <img
      src={resolvedUrl}
      alt={alt}
      className={`object-cover object-center ${className}`}
      onError={() => setError(true)}
    />
  );
};

export default ListingThumbnail;
